import React, { useEffect, useState } from "react";
import axiosInstance from "../../../../../interceptor/axiosInstance";
import { useLocation } from "react-router-dom";
import './PreviewQuestion.css';
import BackButton from "../../../../BackButton";

// Skeleton Loader Component
const SkeletonQuestion = () => (
    <div className="preview-question-skeleton-card">
        <div className="preview-question-skeleton-title"></div>
        <div className="preview-question-skeleton-image"></div>
        <div className="preview-question-skeleton-options">
            {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="preview-question-skeleton-option"></div>
            ))}
        </div>
    </div>
);

// Empty State Component
const EmptyState = () => (
    <div className="preview-question-empty-state">
        <div className="preview-question-empty-icon">📝</div>
        <h3 className="preview-question-empty-title">No Questions Available</h3>
        <p className="preview-question-empty-message">
            This segment doesn't contain any questions yet.
        </p>
    </div>
);

const PreviewQuestion = () => {
    const location = useLocation();
    const { testId } = location.state || {};
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const baseURL = axiosInstance.defaults.baseURL;
    const optionLabels = ["A", "B", "C", "D", "E", "F"];

    useEffect(() => {
        if (testId) {
            setLoading(true);
            axiosInstance.get(`assessments/segments/${testId}/`)
                .then(response => {
                    if (response.data.status === "success") {
                        setQuestions(response.data.data.questions || []);
                    }
                    setLoading(false);
                })
                .catch(error => {
                    console.error("Error fetching questions:", error);
                    setLoading(false);
                });
        }
    }, [testId]);

    return (
        <div className="preview-question-page-container">
            {/* Header */}
            <div className="preview-question-header">
                <div className="preview-question-header-left">
                    <BackButton />
                    <h1 className="preview-question-page-title">Preview Questions</h1>
                    {!loading && questions.length > 0 && (
                        <span className="preview-question-count-badge">
                            {questions.length} {questions.length === 1 ? 'Question' : 'Questions'}
                        </span>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="preview-question-content">
                {loading ? (
                    <>
                        {Array.from({ length: 3 }).map((_, idx) => (
                            <SkeletonQuestion key={idx} />
                        ))}
                    </>
                ) : questions.length > 0 ? (
                    questions.map((question, qIndex) => (
                        <div key={question.id} className="preview-question-card">
                            {/* Question Header */}
                            <div className="preview-question-card-header">
                                <span className="preview-question-number">Question {qIndex + 1}</span>
                                <span className={`preview-question-type-badge preview-question-type-${question.question_type.toLowerCase()}`}>
                                    {question.question_type.toUpperCase()}
                                </span>
                            </div>

                            {/* Question Text */}
                            <h2 className="preview-question-text">{question.text}</h2>

                            {/* Question Image */}
                            {question.image && (
                                <div className="preview-question-image-wrapper">
                                    <img
                                        src={`${baseURL}${question.image}`}
                                        alt="Question"
                                        className="preview-question-image"
                                    />
                                </div>
                            )}

                            {/* Options or Subjective Input */}
                            <div className="preview-question-options-section">
                                {question.question_type.toUpperCase() === "SUBJECTIVE" ? (
                                    <div className="preview-question-subjective-wrapper">
                                        <label className="preview-question-subjective-label">
                                            Student's Answer:
                                        </label>
                                        <textarea
                                            className="preview-question-subjective-input"
                                            placeholder="This is where students will type their answer..."
                                            rows={6}
                                            disabled
                                        />
                                    </div>
                                ) : question.options?.length > 0 ? (
                                    <div className="preview-question-options-grid">
                                        {question.options.map((option, oIndex) => (
                                            <div
                                                key={option.id}
                                                className={`preview-question-option ${option.is_correct ? 'preview-question-option-correct' : ''}`}
                                            >
                                                <div className="preview-question-option-header">
                                                    <span className="preview-question-option-label">
                                                        {optionLabels[oIndex]}
                                                    </span>
                                                    <input
                                                        type={["MCSR", "EITHEROR"].includes(question.question_type.toUpperCase()) ? "radio" : "checkbox"}
                                                        name={`question-${question.id}`}
                                                        disabled
                                                        checked={option.is_correct}
                                                        readOnly
                                                        className="preview-question-option-input"
                                                    />
                                                </div>
                                                <div className="preview-question-option-content">
                                                    <p className="preview-question-option-text">{option.text}</p>
                                                    {option.image && (
                                                        <img
                                                            src={`${baseURL}${option.image}`}
                                                            alt="Option"
                                                            className="preview-question-option-image"
                                                        />
                                                    )}
                                                </div>
                                                {option.is_correct && (
                                                    <div className="preview-question-correct-indicator">
                                                        ✓ Correct Answer
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="preview-question-no-options">No options available for this question</p>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <EmptyState />
                )}
            </div>
        </div>
    );
};

export default PreviewQuestion;
